import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel, { EmblaOptionsType, UseEmblaCarouselType } from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { GqlImageBanner } from 'types/graphql';

type EmblaViewportRefType = UseEmblaCarouselType[0];
type ButtonScrollEventHandler = (event: React.MouseEvent) => void;

export type CarouselOptions = EmblaOptionsType & { banners: GqlImageBanner[] | null };

type UseImageBannerParams = {
  carouselOptions: CarouselOptions;
};

type UseImageBannerReturn = {
  carouselRef: EmblaViewportRefType;
  prevBtnEnabled: boolean;
  nextBtnEnabled: boolean;
  selectedIndex: number;
  scrollSnaps: number[] | null;
  scrollPrev: ButtonScrollEventHandler;
  scrollNext: ButtonScrollEventHandler;
  scrollTo: (index: number) => void;
};

export function useImageBanner({ carouselOptions }: UseImageBannerParams): UseImageBannerReturn {
  const autoplay = Autoplay({ delay: 6000, stopOnInteraction: false, stopOnMouseEnter: true });

  const [emblaRef, embla] = useEmblaCarousel({ ...carouselOptions }, [autoplay]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[] | null>(null);

  const { banners } = carouselOptions;

  const onSelect = useCallback(() => {
    if (!embla) {
      return;
    }

    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla]);

  useEffect(() => {
    if (!embla) {
      return;
    }

    embla.reInit();
    onSelect();
    const initialScrollSnaps = embla.scrollSnapList();
    setScrollSnaps(initialScrollSnaps.length === 1 ? null : initialScrollSnaps);
    embla.on(`select`, onSelect);
  }, [embla, onSelect, banners]);

  const scrollPrev = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      embla?.scrollPrev();
    },
    [embla]
  );
  const scrollNext = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      embla?.scrollNext();
    },
    [embla]
  );

  const scrollTo = useCallback((index: number) => embla?.scrollTo(index), [embla]);

  return {
    carouselRef: emblaRef,
    prevBtnEnabled,
    nextBtnEnabled,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
  };
}
